import { useState } from 'react';
import { createCustomerAccount, type CustomerAccountRequest } from '../../lib/oem-client';
import { runtimeMode, type OemContext } from '../../lib/auth';
import { roleLabel } from './display-copy';
const roles = [
  'customer-requester',
  'customer-sensory',
  'customer-designer',
  'customer-commercial',
  'customer-finance',
];
export function CustomerAccountForm({ context }: { context: OemContext }) {
  const [name, setName] = useState(''),
    [username, setUsername] = useState(''),
    [initialPassword, setInitialPassword] = useState(''),
    [role, setRole] = useState('customer-requester'),
    [evidence, setEvidence] = useState('สร้างบัญชีผู้ติดต่อลูกค้าสำหรับโครงการนี้');
  const [expires, setExpires] = useState(() =>
    new Date(
      Math.min(
        Date.now() + 86400000,
        context.validUntil ? Date.parse(context.validUntil) : Infinity,
      ) -
        new Date().getTimezoneOffset() * 60000,
    )
      .toISOString()
      .slice(0, 16),
  );
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [link, setLink] = useState<string | null>(null);
  const [pending, setPending] = useState<CustomerAccountRequest | null>(null);
  async function submit() {
    if (runtimeMode !== 'connected' || !navigator.onLine) return;
    setBusy(true);
    setLink(null);
    setMessage('');
    const body = pending ?? {
      projectId: context.projectId,
      idempotencyKey: crypto.randomUUID(),
      name,
      username: username.trim().toLowerCase(),
      initialPassword,
      role,
      evidence,
      expiresAt: new Date(expires).toISOString(),
    };
    setPending(body);
    try {
      const result = await createCustomerAccount(body);
      setInitialPassword('');
      setPending(null);
      setLink(result.username);
      setMessage('สร้างบัญชีแล้ว ผู้รับตั้งรหัสผ่านส่วนตัวและรับคำเชิญก่อนใช้งาน');
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      setMessage(
        code === 'existing_account'
          ? 'ชื่อนี้มีบัญชีแล้ว กรุณาเลือกชื่ออื่น'
          : code === 'operation_busy'
            ? 'คำขอนี้กำลังดำเนินการ กรุณาลองคำขอเดิมอีกครั้ง'
            : 'สร้างบัญชีไม่สำเร็จ ลองคำขอเดิมอีกครั้ง หรือติดต่อผู้ดูแล',
      );
    } finally {
      setBusy(false);
    }
  }
  const locked = busy || Boolean(pending);
  return (
    <section className="card">
      <h2>สร้างบัญชีลูกค้า</h2>
      <p>
        ทีมงานกำหนดชื่อผู้ใช้และรหัสเริ่มต้นให้ลูกค้า
        ลูกค้าตั้งรหัสผ่านส่วนตัวเมื่อเข้าสู่ระบบครั้งแรกสำหรับ {context.label}
        สิทธิ์เริ่มเมื่อรับคำเชิญและหมดอายุภายใน 7 วันตามสิทธิ์ผู้เชิญ
        ให้รหัสเริ่มต้นกับผู้รับผ่านช่องทางส่วนตัว ไม่มีการส่งอีเมลอัตโนมัติ
      </p>
      <label>
        ชื่อผู้ติดต่อ
        <input
          value={name}
          disabled={locked}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
        />
      </label>
      <label>
        ชื่อผู้ใช้สำหรับเข้าสู่ระบบ
        <input
          autoComplete="username"
          autoCapitalize="none"
          maxLength={40}
          value={username}
          disabled={locked}
          onChange={(e) => setUsername(e.target.value)}
        />
      </label>
      <label>
        รหัสผ่านเริ่มต้น (อย่างน้อย 12 ตัวอักษร)
        <input
          type="password"
          autoComplete="new-password"
          minLength={12}
          maxLength={1024}
          value={initialPassword}
          disabled={locked}
          onChange={(e) => setInitialPassword(e.target.value)}
        />
      </label>
      <p>เข้าถึงโครงการได้ถึง {new Date(expires).toLocaleString('th-TH')} · ไม่เกิน 7 วัน</p>
      <details>
        <summary>การเข้าถึงโครงการ</summary>
        <label>
          หน้าที่ในโครงการ
          <select value={role} disabled={locked} onChange={(e) => setRole(e.target.value)}>
            {roles.map((r) => (
              <option value={r} key={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        </label>
        <label>
          เหตุผลในการให้สิทธิ์
          <textarea
            value={evidence}
            disabled={locked}
            onChange={(e) => setEvidence(e.target.value)}
            maxLength={4000}
          />
        </label>
        <label>
          เข้าถึงโครงการได้ถึง (เวลาท้องถิ่น)
          <input
            type="datetime-local"
            value={expires}
            disabled={locked}
            onChange={(e) => setExpires(e.target.value)}
          />
        </label>
      </details>
      <button
        className="action"
        disabled={
          runtimeMode !== 'connected' ||
          busy ||
          !name ||
          !username ||
          initialPassword.length < 12 ||
          !evidence ||
          !navigator.onLine
        }
        onClick={() => void submit()}
      >
        {pending ? 'ลองคำขอเดิมอีกครั้ง' : 'สร้างบัญชีลูกค้า'}
      </button>
      {pending ? (
        <button
          disabled={busy}
          onClick={() => {
            setPending(null);
            setInitialPassword('');
            setLink(null);
            setMessage('');
          }}
        >
          เริ่มคำขอใหม่
        </button>
      ) : null}
      {link ? (
        <p role="status">
          ชื่อผู้ใช้: {link} · ลูกค้าต้องเปลี่ยนรหัสผ่านเริ่มต้นและรับคำเชิญก่อนเห็นโครงการ
        </p>
      ) : null}
      {message ? <p role="status">{message}</p> : null}
    </section>
  );
}
